import { isPlatformBrowser } from '@angular/common';
import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';
import { setOrderedId } from 'src/app/store/actions';
import {
  selectLoggedInEmployeeAsArray,
  selectOrderPlanningReportsByPeriodAndStatus,
  selectOrderReport,
  selectPreferredDeliveryPeriods,
} from 'src/app/store/selectors';

import { OrderLineStatus, OrderReport } from '../../models';

import { SwitchTranslationService } from '../../services/switch-translation.service';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  standalone: false,
})
export class ReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly platform = inject(PLATFORM_ID);
  protected readonly router = inject(Router);
  private readonly locale = inject(LocaleService);
  private readonly switchTranslation = inject(SwitchTranslationService);

  showCompleteReport$ = this.route.url.pipe(map((url) => url[0].path === 'complete-report'));
  orderReports$: Observable<OrderReport[]> = of([]);
  pageData$ = this.showCompleteReport$.pipe(
    map((showComplete) => ({
      title: showComplete ? this.switchTranslation.get('completeReport') : this.switchTranslation.get('orderReport'),
      emptyTitle: showComplete ? 'noCompleteOrderReportTitle' : 'noOrderReportsAvailableTitle',
      emptyDescription: showComplete ? null : this.switchTranslation.get('noOrderReportsDescription'),
      primaryActionText: showComplete ? 'print' : this.switchTranslation.get('printOrderReport'),
    })),
  );
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  location$ = this.store.pipe(select(UserSelectors.selectLocation));
  year$ = this.store.pipe(
    select(selectPreferredDeliveryPeriods),
    filter((data) => !!data),
    map((data) => data.year),
  );
  pageTitle$ = combineLatest([this.pageData$, this.customer$, this.year$]).pipe(
    map(([pageData, customer, year]) => `${customer?.name} ${this.locale.translate(pageData?.title)} ${year}`),
  );
  displayedColumns: string[] = ['articleName', 'articleCode', 'packaging', 'orderQuantity'];
  displayedColumnsAlt: string[] = ['articleName', 'articleCode', 'requisite'];
  today = new Date();
  orderLineStatus = OrderLineStatus;
  private readonly subscription = new Subscription();

  ngOnInit(): void {
    this.subscription.add(
      this.route.paramMap.subscribe((paramMap) => {
        const orderParam = paramMap.get('orderedId') as unknown;

        if (!orderParam) {
          return;
        }

        const orderedId = Number(orderParam);
        this.store.dispatch(setOrderedId({ orderedId }));
      }),
    );

    this.subscription.add(
      this.showCompleteReport$.subscribe((showComplete) => {
        if (showComplete) {
          this.orderReports$ = this.store.pipe(select(selectOrderPlanningReportsByPeriodAndStatus));
        } else {
          this.orderReports$ = this.store.pipe(select(selectOrderReport));
        }
      }),
    );

    this.subscription.add(
      this.pageTitle$.subscribe((pageTitle) => {
        this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  print() {
    if (isPlatformBrowser(this.platform)) {
      window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }
}
